import type { TitleFilters } from "@/utils/catalog";
import { allGenres, allRegions, allYears, sortOptions } from "@/utils/catalog";

interface FilterBarProps {
  filters: TitleFilters;
  onChange: (next: Partial<TitleFilters>) => void;
  onReset: () => void;
  resultCount: number;
}

export default function FilterBar({ filters, onChange, onReset, resultCount }: FilterBarProps) {
  const groups: {
    key: "year" | "region" | "genre";
    label: string;
    options: string[];
  }[] = [
    { key: "genre", label: "类型", options: allGenres },
    { key: "region", label: "地区", options: allRegions },
    { key: "year", label: "年代", options: allYears },
  ];

  const activeCount = [
    filters.year !== "全部",
    filters.region !== "全部",
    filters.genre !== "全部",
  ].filter(Boolean).length;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
      <div className="divide-y divide-background-200">
        {groups.map((group) => (
          <div key={group.key} className="flex items-start gap-2 md:gap-3 px-3 md:px-5 py-3">
            <span className="w-10 shrink-0 text-xs text-foreground-500 pt-1.5">{group.label}</span>
            <div className="flex-1 flex flex-wrap gap-1.5">
              {group.options.map((value) => {
                const active = filters[group.key] === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onChange({ [group.key]: value } as Partial<TitleFilters>)}
                    className={`h-7 px-2.5 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
                      active
                        ? "bg-accent-500 text-primary-950 font-medium"
                        : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
                    }`}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 px-3 md:px-5 py-3 bg-background-100 border-t border-background-200">
        <div className="flex items-center gap-2 text-xs text-foreground-600">
          <i className="ri-filter-3-line w-4 h-4 flex items-center justify-center text-foreground-500" />
          共 <span className="font-label font-semibold text-foreground-950">{resultCount}</span> 部作品
          {activeCount > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-accent-100 text-accent-800 text-[11px]">
              {activeCount} 个筛选条件
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onReset}
              className="h-8 px-3 rounded-md text-xs text-foreground-600 border border-background-300 hover:text-foreground-950 hover:border-secondary-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line mr-1" />
              重置筛选
            </button>
          )}
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-50 border border-background-200">
            {sortOptions.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => onChange({ sort: s.value })}
                className={`px-3 h-7 rounded-full text-xs transition-colors whitespace-nowrap cursor-pointer ${
                  filters.sort === s.value
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}